import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { z } from "zod";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { CreatePasswordForm } from "@/components/auth/create-password/create-password-form";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import { usePasswordReset } from "@/hooks/use-password-reset";
import { useStytchErrorDrawer } from "@/hooks/use-stytch-error-drawer";
import { returnToAtom } from "@/lib/atoms/return-to-atom";
import { guardPasswordResetToken } from "@/lib/guards/password-reset-guard";
import { isPasswordResetTokenError } from "@/lib/mappers/stytch-error";
import { isInternalPath } from "@/lib/utils/is-internal-path";

import type { JSX } from "react";

const createPasswordSearch = z.object({
  token: z.string().optional(),
  stytch_token_type: z.string().optional(),
  returnTo: z.string().optional(),
});

const CreatePasswordPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { token }: z.infer<typeof createPasswordSearch> = useSearch({ strict: false });
  const { handleStytchError } = useStytchErrorDrawer();
  const { resetPasswordWithToken } = usePasswordReset();

  const handleSubmit = async (password: string): Promise<void> => {
    try {
      await resetPasswordWithToken(String(token), password);

      void navigate({ to: "/auth/password-reset/password-changed" });
    } catch (error) {
      handleStytchError(error, {
        fallback: "Não foi possível redefinir a senha. Tente novamente.",
        onClose: isPasswordResetTokenError(error)
          ? () => void navigate({ to: "/auth/password-reset/forgot-password" })
          : undefined,
      });
    }
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={1} />
      </FlowHeader>

      <div className="flex flex-col flex-1 justify-between p-6 gap-6">
        <div>
          <PageTitle className="pt-0">Criar nova senha</PageTitle>
          <PageDescription>
            Essa será a nova senha para acessar sua conta, lembre-se que deve conter pelo
            menos 8 caracteres.
          </PageDescription>
        </div>

        <CreatePasswordForm onSubmit={handleSubmit} />
      </div>
    </AuthPageLayout>
  );
};

export const Route = createFileRoute("/auth/password-reset/create-password")({
  validateSearch: createPasswordSearch,
  beforeLoad: ({ search }) => {
    guardPasswordResetToken(search.token);

    if (search.returnTo && isInternalPath(search.returnTo)) {
      getDefaultStore().set(returnToAtom, search.returnTo);
    }
  },
  component: CreatePasswordPage,
});
